class Drawer extends HTMLElement {
  constructor() {
    super();
    this.setListeners()
    this.isDesktop = window.innerWidth > 1024;
  }

  setListeners() {

    this.details = this.querySelector('[data-details]')
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('[data-overlay]')?.addEventListener('click', () => {
      this.close.bind(this)()
    });
    const closeBtns = this.querySelectorAll('[data-drawer-close]');
    closeBtns?.forEach(btn => btn.addEventListener('click', this.close.bind(this)));
  }

  setIconAccessibility(detailsLink) {
    this.details.classList.add(this.isDesktop ? 'desktop-drawer' : 'mobile-drawer');

    detailsLink.setAttribute('role', 'button');
    detailsLink.setAttribute('aria-haspopup', 'dialog');
    detailsLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(detailsLink);
    });

    this.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        this.open(this);
      }
    });
  }


  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate');
      this.setAttribute('open', '');
    });

    this.addEventListener(
      'transitionend',
      () => {
        trapFocus(this, this.details);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

  }

  close() {
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

class SizeDrawer extends Drawer {
  constructor() {
    super();
    this.setUpListeners();

  }

  listenerUnsubscriber = undefined;

  connectedCallback() {
    this.listenerUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
      this.setUpListeners()
    })
  }

  disconnectedCallback() {
    if (this.listenerUnsubscriber) {
      this.listenerUnsubscriber();
    }
  }

  setUpListeners() {
    const opener = document.querySelector('[data-size-guide]');
    if (!opener) return;
    this.setIconAccessibility(opener);
  }

}


customElements.define('size-drawer', SizeDrawer);



class CartDrawer extends Drawer {
  constructor() {
    super();

    const opener = document.querySelector('#cart-icon-bubble');
    this.setIconAccessibility(opener);
    this.setContentHeight()

    subscribe(PUB_SUB_EVENTS.relatedProductsUploaded, () => {
      this.setContentHeight()
    })


    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.setListeners()
    })

  }

  open() {
    super.open();
    this.querySelector('[data-drawer-close]')?.focus();
    publish(PUB_SUB_EVENTS.openCartDrawer)
  }

  
  setContentHeight() {
    const drowerHeaderH = this.querySelector('.drawer__header').offsetHeight;
    const drowerFooterH = this.querySelector('[data-drawer-footer]')?.offsetHeight || 0;

    const drowerContentH = parseInt(window.innerHeight - drowerFooterH - drowerHeaderH);
    document.documentElement.style.setProperty('--cart-drawer-content-height', `${drowerContentH || 0}px`);
  }

  /**
   * @param {object} parsedState
   * @param {{ source?: string, productVariantId?: string }} [publishOptions]
   */
  renderContents(parsedState, publishOptions = {}) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.setContentHeight();

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: publishOptions.source || 'cart-drawer',
      productVariantId: publishOptions.productVariantId,
      cartData: parsedState,
    });

    setTimeout(() => {
      this.querySelector('[data-overlay]').addEventListener('click', this.close.bind(this));
      this.open();
      super.setListeners();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const target = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-related-products',
        selector: `#cart-related-products`,
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '[data-details]',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);



class SearchDrawer extends Drawer {
  constructor() {
    super();

    const openers = document.querySelectorAll('[data-search-opener]');

    openers.forEach((opener) => {
      this.setIconAccessibility(opener);
    });

    this.selectors = {
      searchBar: '[data-interactive-searchbar]',
      searchResults: '[data-searchdrawer-products]',
      viewAll: '[data-view-all]',
      searchSuggestions: '[data-search-suggestion]'
    }
    this.typingTimer = null;
    this.doneTypingInterval = 500;
    this.onDoneTyping = this.onDoneTyping.bind(this);

    this.searchTerms = this.dataset.currentSearchterms
  }

  get searchUrl() {
    return `${routes.search_url}?q=${encodeURIComponent(this.searchTerms)}&type=product&${this.currentGenderFilter || ''}`;
  }

  connectedCallback() {
    this.initSearchBar();
    this.initGenderFilters();
    this.initSearchSuggestions();
  }

  open() {
    super.open();
    this.isDesktop && this.querySelector(this.selectors.searchBar)?.focus()

  }

  /**
   * Add a listener on the input to update the search when the user stops typing 
   */
  initSearchBar() {
    const searchBar = this.querySelector(this.selectors.searchBar);
    if (!searchBar) return;

    searchBar.addEventListener('input', () => {
      clearTimeout(this.typingTimer);
      this.typingTimer = setTimeout(this.onDoneTyping, this.doneTypingInterval);
    });

    searchBar.addEventListener('keydown', () => {
      clearTimeout(this.typingTimer);
    });
  }

  /**
   * - Retrieve if there's an active gender filter and save the value.
   * - Add listeners on the filters to update the current one and refresh the search results
   */
  initGenderFilters() {
    const genderPills = this.querySelectorAll('[data-searchbar-gender-pill]')
    if (!genderPills?.length) return;

    genderPills.forEach((pill) => {
      if (pill.checked) {
        this.currentGenderFilter = pill.value;
      }

      pill.addEventListener('click', (event) => {
        this.currentGenderFilter = event.target.value;
        this.querySelectorAll('[data-current-gender-text]').forEach((span) => span.innerText = event.target.value.split('=').at(-1));
        this.handleSearch();
      })
    });
  }


  /**
   * - Add an event listener on the suggestions list buttons
   * - When a suggestion gets clicked set it as search terms in the search bar and update the results
   */
  initSearchSuggestions() {

    this.addEventListener('click', (e) => {
      if (e.target.matches('[data-search-suggestion]')) {
        this.querySelector(this.selectors.searchBar).value = e.target.value;
        this.onDoneTyping();
      }
    });

  }

  /**
   * Retrieve the value of the search terms from the searchbar and use it to update
   * the results
   */
  onDoneTyping() {
    this.searchTerms = this.querySelector(this.selectors.searchBar)?.value;
    this.querySelectorAll("[data-view-all]>button").forEach((el) =>
      el.toggleAttribute('disabled', !this.searchTerms)
    );
    this.querySelector('[data-current-searchterms]').innerText = `"${this.searchTerms}"`;
    this.handleSearch();
  }

  /**
   * Updates the "View all results" buttons with the correct updated URL
   */
  updateViewButtonsUrl() {
    this.querySelectorAll(this.selectors.viewAll).forEach(element => {
      element.setAttribute('href', this.searchUrl);
    });
  }

  /**
   * - Fetch a search call and use the parsed result to update the results.
   * - Once the fetch is completed update the "View all results" button URL
   */
  handleSearch() {
    fetch(`${this.searchUrl}&view=search-drawer`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(this.selectors.searchResults).innerHTML;

        this.querySelector(this.selectors.searchResults).innerHTML = resultsMarkup;
        this.updateViewButtonsUrl();
        this.initSearchSuggestions();
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('search-drawer', SearchDrawer);

class NotifyDrawer extends Drawer {
  constructor() {
    super();

    this.email = this.querySelector('input[name="email"]');
    this.variant = this.querySelector('input[name="variant"]');
    this.privacy = this.querySelector('input[name="privacy"]');

    this.setContentHeight();

    subscribe(PUB_SUB_EVENTS.onNotifyMe, () => {
      this.open();
    })

    this.form = this.querySelector('form');
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.onSubmit();
    });

  }

  open() {
    super.open();
    this.email.focus();
  }

  setContentHeight() {
    const drowerHeaderH = this.querySelector('[data-drawer-header]').offsetHeight;
    const drowerFooterH = this.querySelector('[data-drawer-footer]').offsetHeight;
    const drowerTitleH = this.querySelector('[data-drawer-title]').offsetHeight
    const drowerContentH = parseInt(window.innerHeight - drowerFooterH - drowerHeaderH - drowerTitleH);

    document.documentElement.style.setProperty('--notify-content-height', `${drowerContentH || 0}px`);
  }

  onSubmit() {
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }
    this.buildData();
  }


  async buildData() {

    const url = `${window.klaviyoVariables.api_url}${window.klaviyoVariables.company_id}`;
    const data = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: `${this.email.value}`
              }
            }
          }
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${this.variant.value}`
            }
          }
        }
      }
    }

    const notifyBtn = this.querySelector('[data-notify-button]');

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "accept": "application/vnd.api+json",
          "content-type": "application/vnd.api+json",
          "revision": '2025-01-15',
        },
        body: JSON.stringify(data),
      })

      if (response.status == '400') {
        notifyBtn.innerText = window.notifyMe.error;
        return
      }

      notifyBtn.innerText = window.notifyMe.success
    } catch (e) {
      notifyBtn.innerText = window.notifyMe.error
    }
  }
}

customElements.define('notify-drawer', NotifyDrawer);


class LocalizationDrawer extends Drawer {
  constructor() {
    super();

    document.querySelectorAll('[data-localization-opener]')?.forEach((opener) => {
      this.setIconAccessibility(opener);
    });

    this.countries = this.querySelectorAll('[data-country-name]');
  }

  connectedCallback() {
    this.searchInput = this.querySelector('[data-search-country]');
    this.searchInput?.addEventListener('input', (event) => {
      this.search(event.target.value);
    });

    this.setContentHeight();
  }

  open() {
    super.open();
    publish(PUB_SUB_EVENTS.localizationDrawerOpen);

    this.isDesktop && this.searchInput?.focus()

  }


  setContentHeight() {
    const headerElement = this.querySelector('[data-header]');
    const footerElement = this.querySelector('[data-footer]');
    const inputElement = this.querySelector('[data-search-wrapper]');

    const header = headerElement ? parseFloat(getComputedStyle(headerElement).height) : 0;
    const footer = footerElement ? parseFloat(getComputedStyle(footerElement).height) : 0;
    const input = inputElement ? parseFloat(getComputedStyle(inputElement).height) : 0;

    const content = parseInt(window.innerHeight - footer - header - input);
    document.documentElement.style.setProperty('--drawer-localization-height', `${content || 0}px`);
  }

  search(value) {
    const searchTerm = value.toLowerCase();

    this.countries.forEach((country) => {
      const countryName = country.dataset.countryName.toLowerCase();
      country.toggleAttribute('hidden', !countryName.includes(searchTerm));
    });
  }

}

customElements.define('localization-drawer', LocalizationDrawer);
